import { useEffect, useState } from "react";
import { Robot, CheckCircle, X, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function TrainerBot() {
  const [proposals, setProposals] = useState([]);
  const [view, setView] = useState("pending");

  const load = async () => {
    try {
      const { data } = await api.get("/bot/proposals");
      setProposals(data);
    } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const approve = async (id, idx) => {
    try {
      await api.post(`/bot/proposals/${id}/approve?slot_index=${idx}`);
      toast.success("Approved — session moved & client notified");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/bot/proposals/${id}/reject`);
      toast.success("Rejected — client told to reach out directly");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = proposals.filter((p) => view === "all" || p.status === view);

  return (
    <div className="space-y-8" data-testid="trainer-bot">
      <div>
        <div className="tracker text-brand mb-3 flex items-center gap-2">
          <Robot size={14} weight="duotone" /> [ AI Bot Inbox ]
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Reschedule requests.</h1>
        <p className="text-zinc-500 text-xs mt-2">Clients chat with Traina Bot to propose reschedules. You approve a slot — the session moves and they're notified automatically.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: "pending", label: "Pending", count: proposals.filter((p) => p.status === "pending").length },
          { key: "approved", label: "Approved", count: proposals.filter((p) => p.status === "approved").length },
          { key: "rejected", label: "Rejected", count: proposals.filter((p) => p.status === "rejected").length },
          { key: "all", label: "All", count: proposals.length },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`tracker px-5 py-2 border ${view === v.key ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
            data-testid={`bot-view-${v.key}`}
          >
            {v.label} ({v.count})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-flat p-16 text-center">
          <Robot size={36} weight="duotone" className="text-zinc-700 mx-auto mb-4" />
          <div className="tracker text-zinc-500">No {view === "all" ? "" : view} requests</div>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((p) => (
            <div key={p.id} className="card-flat p-6" data-testid={`proposal-${p.id}`}>
              <div className="grid lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-2 tracker mb-2 flex-wrap">
                    <ChatCircle size={14} weight="duotone" className="text-brand" />
                    From {p.client_name}
                    {p.action === "cancel" && (
                      <span className="tracker px-2 py-0.5 border border-[#FF3B30]/40 text-[#FF6B61] text-[9px]">CANCEL</span>
                    )}
                    {p.action === "cancel" && p.cancel_scope === "future" && (
                      <span className="tracker px-2 py-0.5 border border-amber-500/50 text-amber-400 text-[9px]" data-testid={`scope-badge-${p.id}`}>
                        SERIES · FUTURE
                      </span>
                    )}
                    {p.action === "cancel" && p.cancel_scope === "all" && (
                      <span className="tracker px-2 py-0.5 border border-[#FF3B30]/60 bg-[#FF3B30]/10 text-[#FF6B61] text-[9px]" data-testid={`scope-badge-${p.id}`}>
                        SERIES · ALL
                      </span>
                    )}
                  </div>
                  <div className="text-sm italic text-zinc-300 mb-3">"{p.client_message}"</div>
                  <div className="tracker text-zinc-500 mb-1">Current session</div>
                  <div className="font-mono text-sm">{p.session_type} · {new Date(p.original_start_time).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
                  {p.preferred_when && (
                    <>
                      <div className="tracker text-zinc-500 mt-3 mb-1">Client wants</div>
                      <div className="text-sm text-brand-light">{p.preferred_when}</div>
                    </>
                  )}
                </div>

                <div className="lg:col-span-6">
                  {p.action === "cancel" ? (
                    <div>
                      <div className="tracker text-zinc-500 mb-2">Action</div>
                      {(() => {
                        const scope = p.cancel_scope || "this";
                        const count = p.cancel_siblings_count || 1;
                        const curr = (p.currency || "usd").toLowerCase();
                        const sym = curr === "gbp" ? "£" : curr === "eur" ? "€" : "$";
                        const hasRefund = p.estimated_refund !== undefined && p.estimated_refund !== null;
                        const refund = hasRefund ? Number(p.estimated_refund).toFixed(2) : null;
                        const scopeLine =
                          scope === "all"
                            ? `Entire series — ${count} sessions cancelled`
                            : scope === "future"
                            ? `This + ${Math.max(0, count - 1)} future occurrences`
                            : "Single session";
                        const isSeries = scope !== "this";
                        return (
                          <div
                            className={`mb-3 p-3 border text-xs ${isSeries ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-white/10 bg-white/5"}`}
                            data-testid={`scope-summary-${p.id}`}
                          >
                            <div className="tracker text-zinc-500 mb-1">Scope</div>
                            <div className="font-mono text-sm text-zinc-100">{scopeLine}</div>
                            <div className="tracker text-zinc-500 mt-2 mb-1">Pot refund</div>
                            <div className="font-mono text-sm text-emerald-400" data-testid={`scope-refund-${p.id}`}>
                              {refund ? `${sym}${refund}` : "—"}
                            </div>
                            {isSeries && (
                              <div className="tracker text-[#FF6B61] mt-2 text-[10px]">
                                ! Approving will cancel multiple sessions at once.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <button
                        disabled={p.status !== "pending"}
                        onClick={() => approve(p.id, 0)}
                        className={`w-full p-4 border text-left ${
                          p.status === "approved"
                            ? "border-emerald-500/40 bg-emerald-500/10"
                            : p.status === "pending"
                            ? "border-[#FF3B30]/40 hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 cursor-pointer"
                            : "border-white/5 opacity-50"
                        }`}
                        data-testid={`approve-cancel-${p.id}`}
                      >
                        <div className="font-display text-base mb-1">
                          {(p.cancel_scope || "this") === "all"
                            ? `Approve series cancellation (${p.cancel_siblings_count || 1} sessions)`
                            : (p.cancel_scope || "this") === "future"
                            ? `Approve this + ${Math.max(0, (p.cancel_siblings_count || 1) - 1)} future`
                            : "Approve cancellation"}
                        </div>
                        <div className="tracker text-zinc-400">Sessions marked cancelled · pot refunded automatically</div>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="tracker text-zinc-500 mb-2">Proposed slots</div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        {(p.proposed_slots || []).map((s, idx) => {
                          const isApproved = p.status === "approved" && p.approved_slot === s;
                          return (
                            <button
                              key={s}
                              disabled={p.status !== "pending"}
                              onClick={() => approve(p.id, idx)}
                              className={`p-3 border text-left ${
                                isApproved
                                  ? "border-emerald-500/40 bg-emerald-500/10"
                                  : p.status === "pending"
                                  ? "border-white/10 hover:border-brand hover:bg-brand/10 cursor-pointer"
                                  : "border-white/5 opacity-50"
                              }`}
                              data-testid={`approve-slot-${p.id}-${idx}`}
                            >
                              <div className="font-display text-base leading-none mb-1">{new Date(s).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
                              <div className="tracker text-zinc-500">{new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                              {isApproved && <div className="tracker text-emerald-400 mt-2"><CheckCircle size={12} weight="fill" className="inline" /> Approved</div>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="lg:col-span-2 flex flex-col gap-2 justify-center">
                  {p.status === "pending" ? (
                    <button onClick={() => reject(p.id)} className="btn-ghost !text-[10px] justify-center" data-testid={`reject-${p.id}`}>
                      <X size={14} weight="bold" /> Reject all
                    </button>
                  ) : (
                    <span className={`tracker px-3 py-2 border text-center ${p.status === "approved" ? "border-emerald-500/30 text-emerald-400" : "border-zinc-500/30 text-zinc-500"}`}>
                      {p.status}
                    </span>
                  )}
                  <div className="tracker text-zinc-600 text-center">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
